/* eslint-disable react-hooks/exhaustive-deps */
import { useMediaQuery, Grid, Box, useTheme, Typography, Button, Container, CircularProgress } from "@mui/material";
import { useNavigate } from "react-router-dom";
import CustomRightImage from "@components/CustomRightImagev2";
import { useLazyGetTaxSystemConfigQuery } from "@api/applications";
import { useEffect, useState } from "react";
import { TaxSystemConfigResponse } from "@interfaces/index";
import {
  amountToText,
  calculateAmountMonthly,
  formatCurrency,
  generateQuotationAmountMarks,
  generateQuotationTermMarks,
} from "@helpers/funciones";
import { StyledSlider } from "@components/CustomSlider";
import ButtonTypeSelectedV2 from "@components/ButtonTypeSelectedV2";
import useInterfaceConfig from "@hooks/useInterfaceConfig";

const CotizadorProvidencia = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down("md"));
  const interfaceConfig = useInterfaceConfig();

  const [getTaxSystemConfig] = useLazyGetTaxSystemConfigQuery();
  const [configPM, setConfigPM] = useState<TaxSystemConfigResponse | null>(null);
  const [configPFAE, setConfigPFAE] = useState<TaxSystemConfigResponse | null>(null);
  const [showRegimeSelector, setShowRegimeSelector] = useState<boolean>(false);
  const [personType, setPersonType] = useState<string>("PM");

  const [amountMarks, setAmountMarks] = useState<{ value: number; label: string }[]>([]);
  const [termMarks, setTermMarks] = useState<{ value: number; label: string }[]>([]);
  const [cotizadorConfig, setCotizadorConfig] = useState<TaxSystemConfigResponse>({
    id: "",
    code: "",
    product_id: "",
    minAmount: 0,
    maxAmount: 0,
    stepAmount: 0,
    minTerm: 0,
    maxTerm: 0,
    stepTerm: 0,
    defaultAmount: 0,
    defaultTerm: 0,
    tax_system_type: "",
  });

  const [selectedAmount, setSelectedAmount] = useState<number>(cotizadorConfig.defaultAmount);
  const [selectedTerm, setSelectedTerm] = useState<number>(cotizadorConfig.defaultTerm);
  const [monthlyRate, setMonthlyRate] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const { type: cotizadorType, tax_system_codes } = interfaceConfig.cotizador_simple || {};
      const { pm: pmCode, pfae: pfaeCode } = tax_system_codes || {};

      if (cotizadorType === "simple") {
        if (!pmCode || !pfaeCode) {
          throw new Error("PM or PFAE tax system code is required");
        }

        const response_PM = pmCode ? await getTaxSystemConfig(pmCode).unwrap() : null;
        const response_PFAE = pfaeCode ? await getTaxSystemConfig(pfaeCode).unwrap() : null;

        setConfigPM(response_PM);
        setConfigPFAE(response_PFAE);
        setShowRegimeSelector(response_PM && response_PFAE ? true : false);
        setPersonType(response_PM ? "PM" : "PFAE");
      } else {
        throw new Error("Cotizador type not supported");
      }
    } catch (error) {
      console.error("Error fetching tax system configs", error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const continueToTOFU = () => {
    if (!cotizadorConfig.product_id || !selectedAmount || !monthlyRate) {
      return;
    }

    const sourceCode = sessionStorage.getItem("source_code");

    let urlPortal = `/producto/creditosimple/crear_cuenta?tax_system_id=${cotizadorConfig.id}&product_id=${cotizadorConfig.product_id}&person_type=${cotizadorConfig.code}&loan_amount=${selectedAmount}&loan_term=${selectedTerm}&tax_system_type=${cotizadorConfig.tax_system_type}`;

    if (sourceCode) {
      urlPortal += `&source_code=${sourceCode}`;
    }
    navigate(urlPortal);
  };

  useEffect(() => {
    if (personType === "PM" && configPM) {
      setCotizadorConfig(configPM);
    } else if (personType === "PFAE" && configPFAE) {
      setCotizadorConfig(configPFAE);
    }
  }, [personType, configPM, configPFAE]);

  useEffect(() => {
    const { minAmount, maxAmount, minTerm, maxTerm, stepTerm, defaultAmount, defaultTerm } = cotizadorConfig;
    const marksAmount = generateQuotationAmountMarks(minAmount, maxAmount);
    const marksTerm = generateQuotationTermMarks(minTerm, maxTerm, stepTerm);

    setAmountMarks(marksAmount);
    setTermMarks(marksTerm);
    setSelectedAmount(defaultAmount);
    setSelectedTerm(defaultTerm);
  }, [cotizadorConfig]);

  useEffect(() => {
    if (selectedAmount && selectedTerm) {
      const monthly = calculateAmountMonthly(selectedAmount, selectedTerm, interfaceConfig?.interest_rate);
      setMonthlyRate(monthly);
    }
  }, [selectedAmount, selectedTerm]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sourceCode = params.get("code");

    if (sourceCode) {
      sessionStorage.setItem("source_code", sourceCode);
    } else {
      sessionStorage.removeItem("source_code");
    }
  }, []);

  return (
    <Grid container height="100%">
      <Grid item xs={12} md={8}>
        {isLoading ? (
          <Container>
            <Box display="flex" justifyContent="center" alignItems="center" height="100%">
              <CircularProgress size={40} />
            </Box>
          </Container>
        ) : (
          <Container maxWidth={"sm"}>
            <Typography fontWeight={500} variant="h3" mt={4} color="primary.dark" textAlign={"center"}>
              Cotizador
            </Typography>

            <Box>
              {showRegimeSelector && (
                <Box>
                  <Typography fontWeight={500} mt={1} color="primary.dark" textAlign={"center"}>
                    Selecciona tu régimen fiscal
                  </Typography>

                  <Grid container spacing={isMobileOrTablet ? 0 : 2} mt={1} mr={isMobileOrTablet ? 1 : 0}>
                    <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-end"}>
                      <ButtonTypeSelectedV2
                        title="Persona Moral"
                        onClick={() => setPersonType("PM")}
                        isSelected={personType === "PM"}
                      />
                    </Grid>
                    <Grid item xs={6} md={6} display="flex" justifyContent={isMobileOrTablet ? "center" : "flex-start"}>
                      <ButtonTypeSelectedV2
                        title="Persona Física con Actividad Empresarial"
                        onClick={() => setPersonType("PFAE")}
                        isSelected={personType === "PFAE"}
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}

              <Box mt={1} px={isMobileOrTablet ? 3 : 1}>
                <Typography fontWeight={500} color="primary.dark" textAlign={"center"}>
                  Selecciona el monto
                </Typography>

                <Typography fontWeight={700} variant="body2" color="primary.main" textAlign={"center"}>
                  {amountToText(selectedAmount)}
                </Typography>

                <StyledSlider
                  aria-label="amount"
                  min={cotizadorConfig.minAmount}
                  max={cotizadorConfig.maxAmount}
                  step={cotizadorConfig.stepAmount}
                  marks={amountMarks}
                  defaultValue={cotizadorConfig.defaultAmount}
                  value={selectedAmount}
                  onChange={(_event, newValue) => setSelectedAmount(newValue as number)}
                />
              </Box>

              <Box mb={1} px={isMobileOrTablet ? 3 : 1}>
                <Typography fontWeight={500} mb={2} color="primary.dark" textAlign={"center"}>
                  Selecciona el plazo en meses
                </Typography>

                <StyledSlider
                  aria-label="term"
                  min={cotizadorConfig.minTerm}
                  max={cotizadorConfig.maxTerm}
                  step={1}
                  defaultValue={cotizadorConfig.defaultTerm}
                  marks={termMarks}
                  value={selectedTerm}
                  onChange={(_event, newValue) => setSelectedTerm(newValue as number)}
                  valueLabelDisplay="on"
                />
              </Box>

              <Typography fontWeight={700} color="primary.main" textAlign={"center"} mt={2}>
                Pago mensual estimado*
              </Typography>

              <Typography
                mb={2}
                textAlign={"center"}
                variant="h4"
                fontWeight={theme?.palette?.fontWeights?.semibold}
                color="primary.dark"
              >
                {isNaN(monthlyRate) || !isFinite(monthlyRate) ? "0" : formatCurrency(monthlyRate)}{" "}
                <Typography
                  variant="h5"
                  component={"span"}
                  fontWeight={theme?.palette?.fontWeights?.semibold}
                  color="primary.dark"
                >
                  MXN
                </Typography>
              </Typography>

              <Typography
                textAlign={"center"}
                fontWeight={theme?.palette?.fontWeights?.light}
                color="primary.dark"
                mt={2}
                mb={2}
              >
                <small>*Mensualidad estimada con tasa ponderada promedio.</small>
              </Typography>

              <Box display="flex" justifyContent="center" mb={2}>
                <Button variant="default" onClick={continueToTOFU}>
                  Continuar
                </Button>
              </Box>
            </Box>
          </Container>
        )}
      </Grid>
      {!isMobileOrTablet && (
        <Grid item xs={12} md={4}>
          <CustomRightImage />
        </Grid>
      )}
    </Grid>
  );
};

export default CotizadorProvidencia;
